import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { ChevronDown, ChevronRight, Layers, Loader2, Search } from 'lucide-react';
import {
  creativeStrategyApi,
  type StrategyDocketSummary,
  type TaxonomyChild,
  type TaxonomyLibraryRef,
  type TaxonomyView,
} from '@/services/api';

/**
 * The Library admin: Shopify's product taxonomy on the left, and on the right the
 * libraries — the research mined for a category — at or below the chosen category.
 */
export function LibraryBrowser({
  libraries,
  librariesLoading,
}: {
  libraries: StrategyDocketSummary[];
  librariesLoading: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get('category');
  const [view, setView] = useState<TaxonomyView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [children, setChildren] = useState<Record<string, TaxonomyChild[]>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [roots, setRoots] = useState<TaxonomyChild[] | null>(null);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<{ id: string; full_name: string }[]>([]);

  const byId = useMemo(() => new Map(libraries.map((l) => [l.docket_id, l])), [libraries]);

  // The root's children start the tree.
  useEffect(() => {
    creativeStrategyApi
      .taxonomy(null)
      .then((v) => setRoots(v.children))
      .catch((e: Error) => setError(e.message));
  }, []);

  // The selected category's detail; its path is opened in the tree.
  useEffect(() => {
    setView(null);
    creativeStrategyApi
      .taxonomy(selected)
      .then((v) => {
        setView(v);
        if (v.category) setChildren((c) => ({ ...c, [v.category!.id]: v.children }));
        setOpen((o) => {
          const next = { ...o };
          v.path.slice(0, -1).forEach((p) => (next[p.id] = true));
          return next;
        });
      })
      .catch((e: Error) => setError(e.message));
  }, [selected]);

  // Ancestors of the selection need their children loaded to render the open path.
  useEffect(() => {
    view?.path.slice(0, -1).forEach((p) => {
      if (!children[p.id]) {
        creativeStrategyApi.taxonomy(p.id).then((v) => setChildren((c) => ({ ...c, [p.id]: v.children })));
      }
    });
  }, [view, children]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setHits([]);
      return;
    }
    const t = setTimeout(() => {
      creativeStrategyApi
        .searchTaxonomy(query.trim())
        .then((r) => setHits(r.categories))
        .catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const select = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set('category', id);
      else next.delete('category');
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const toggle = (node: TaxonomyChild) => {
    const willOpen = !open[node.id];
    setOpen((o) => ({ ...o, [node.id]: willOpen }));
    if (willOpen && !children[node.id]) {
      creativeStrategyApi.taxonomy(node.id).then((v) => setChildren((c) => ({ ...c, [node.id]: v.children })));
    }
  };

  const renderNodes = (nodes: TaxonomyChild[], depth: number) => (
    <ul>
      {nodes.map((n) => (
        <li key={n.id}>
          <div
            className={clsx(
              'flex items-center gap-1 rounded-md pr-2 py-1 text-[13px] cursor-pointer',
              selected === n.id ? 'bg-navy-900 text-white' : 'hover:bg-slate-100 text-slate-700',
            )}
            style={{ paddingLeft: 4 + depth * 12 }}
          >
            <button
              type="button"
              onClick={() => n.has_children && toggle(n)}
              className={clsx('w-4 shrink-0', !n.has_children && 'invisible')}
              aria-label={open[n.id] ? `Collapse ${n.name}` : `Expand ${n.name}`}
            >
              {open[n.id] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
            <span className="flex-1 truncate" onClick={() => select(n.id)} title={n.name}>
              {n.name}
            </span>
            {n.libraries_below > 0 && (
              <span
                className={clsx(
                  'text-[10px] rounded-full px-1.5 tabular-nums',
                  selected === n.id ? 'bg-white/20' : 'bg-teal-50 text-brand-teal',
                )}
                title="Libraries at or below this category"
              >
                {n.libraries_below}
              </span>
            )}
          </div>
          {open[n.id] && children[n.id] && renderNodes(children[n.id], depth + 1)}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="flex h-full min-h-0">
      <aside className="w-80 shrink-0 border-r border-slate-200 bg-white flex flex-col min-h-0">
        <div className="p-3 border-b border-slate-100">
          <div className="flex items-center rounded-lg border border-slate-300 px-2">
            <Search size={13} className="text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a Shopify category"
              className="w-full px-2 py-1.5 text-sm outline-none"
            />
          </div>
          {hits.length > 0 && (
            <ul className="mt-2 max-h-56 overflow-auto rounded-lg border border-slate-200 text-xs">
              {hits.map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    className="block w-full text-left px-2 py-1.5 hover:bg-slate-50 text-slate-700"
                    onClick={() => {
                      select(h.id);
                      setQuery('');
                    }}
                  >
                    {h.full_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex-1 overflow-auto p-2">
          <button
            type="button"
            onClick={() => select(null)}
            className={clsx(
              'w-full text-left rounded-md px-2 py-1 text-[13px] mb-1',
              !selected ? 'bg-navy-900 text-white' : 'hover:bg-slate-100 text-slate-700',
            )}
          >
            All libraries
          </button>
          {roots ? renderNodes(roots, 0) : (
            <div className="flex items-center text-xs text-slate-400 p-2">
              <Loader2 size={12} className="animate-spin mr-2" /> Loading taxonomy…
            </div>
          )}
        </div>
        {view && (
          <div className="px-3 py-2 border-t border-slate-100 text-[10px] text-slate-400">
            Shopify product taxonomy {view.version}
          </div>
        )}
      </aside>

      <section className="flex-1 min-w-0 overflow-auto p-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
        )}
        {!view || librariesLoading ? (
          <div className="flex items-center text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading the library…
          </div>
        ) : (
          <>
            <nav className="mb-1 text-xs text-slate-500 flex flex-wrap gap-1">
              <button type="button" className="hover:text-navy-900" onClick={() => select(null)}>
                All
              </button>
              {view.path.map((p) => (
                <span key={p.id} className="flex gap-1">
                  <span>›</span>
                  <button type="button" className="hover:text-navy-900" onClick={() => select(p.id)}>
                    {p.name}
                  </button>
                </span>
              ))}
            </nav>
            <h2 className="text-lg font-semibold text-navy-900 mb-4">{view.category?.name ?? 'All libraries'}</h2>

            {view.category ? (
              <>
                <LibraryGroup title="Libraries on this category" refs={view.libraries_here} byId={byId} />
                <LibraryGroup title="Further down this branch" refs={view.libraries_below} byId={byId} />
                {!view.libraries_here.length && !view.libraries_below.length && (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
                    Nothing has been mined for this category yet. A library here would start from a mining plan for
                    a client that sells in it.
                  </div>
                )}
              </>
            ) : (
              <>
                <LibraryGroup
                  title="Placed on a category"
                  refs={libraries
                    .filter((l) => !view.unplaced.some((u) => u.docket_id === l.docket_id))
                    .map((l) => ({ ...l }))}
                  byId={byId}
                />
                <LibraryGroup title="Not on a category" refs={view.unplaced} byId={byId} />
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function LibraryGroup({
  title,
  refs,
  byId,
}: {
  title: string;
  refs: TaxonomyLibraryRef[];
  byId: Map<string, StrategyDocketSummary>;
}) {
  if (!refs.length) return null;
  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{title}</h3>
      <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(22rem,1fr))]">
        {refs.map((r) => (
          <LibraryCard key={r.docket_id} lib={byId.get(r.docket_id)} fallback={r} />
        ))}
      </div>
    </div>
  );
}

/** What research rolled up into one library. */
function LibraryCard({ lib, fallback }: { lib?: StrategyDocketSummary; fallback: TaxonomyLibraryRef }) {
  const name = lib?.name ?? fallback.name;
  const to = `/knowledge/creative-strategy/library/${encodeURIComponent(fallback.docket_id)}`;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-teal-50 text-brand-teal flex items-center justify-center shrink-0">
          <Layers size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <Link to={to} className="font-medium text-navy-900 hover:text-brand-teal">
            {name}
          </Link>
          <div className="text-xs text-slate-400">
            {lib?.shopify_category_path?.join(' › ') || 'No Shopify category'}
          </div>
        </div>
        <span className="text-[11px] font-mono text-slate-400">
          {lib?.pinned_version ? `${lib.pinned_version} pinned` : lib?.served_version ?? '—'}
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
        {[
          ['Buyer concepts', lib?.counts.buyer_concepts],
          ['Ad strategies', lib?.counts.strategies],
          ['Templates', lib?.counts.templates],
        ].map(([label, n]) => (
          <div key={label as string} className="rounded-lg bg-slate-50 py-2">
            <dd className="text-base font-semibold text-navy-900 tabular-nums">{(n as number | undefined) ?? '—'}</dd>
            <dt className="text-[10px] text-slate-500">{label}</dt>
          </div>
        ))}
      </dl>
      <div className="mt-3 space-y-1 text-xs text-slate-600">
        <div>
          <span className="text-slate-400">Clients drawing on it: </span>
          {(lib?.member_clients ?? fallback.member_clients).length
            ? (lib?.member_clients ?? fallback.member_clients).map((c, i) => (
                <span key={c}>
                  {i > 0 && ', '}
                  <Link
                    to={`/knowledge/creative-strategy/dockets/${encodeURIComponent(c)}`}
                    className="text-navy-900 hover:text-brand-teal"
                  >
                    {c}
                  </Link>
                </span>
              ))
            : 'none'}
        </div>
        {!!lib?.amazon_paths?.length && (
          <div>
            <span className="text-slate-400">Review shelves: </span>
            {lib.amazon_paths.map((p) => p.split(' > ').slice(-1)[0]).join(' · ')}
          </div>
        )}
        {(lib?.hidden_count ?? 0) > 0 && <div className="text-amber-700">{lib?.hidden_count} items hidden</div>}
      </div>
      <div className="mt-3 flex gap-3 text-xs">
        <Link to={to} className="text-navy-700 underline">
          Open library
        </Link>
        <Link to={`${to}?tab=runs`} className="text-navy-700 underline">
          Runs
        </Link>
        <Link to={`${to}?tab=templates`} className="text-navy-700 underline">
          Templates
        </Link>
      </div>
    </div>
  );
}
